/**
 * BuildJob Status State Machine - M3.1
 *
 * Enforces legal transitions between build job lifecycle states.
 * The state machine ensures:
 * - No skipping build/test phases
 * - Retries are bounded by max_attempts
 * - Human approval is the only path to APPROVED/PUBLISHED
 * - All transitions are auditable
 */

import { BuildJobStatus } from '../types/buildJob.js';

/**
 * Legal state transitions for build jobs.
 * Key = current status, Value = set of allowed next statuses.
 */
export const BUILD_JOB_ALLOWED_TRANSITIONS: ReadonlyMap<
  BuildJobStatus,
  ReadonlySet<BuildJobStatus>
> = new Map<BuildJobStatus, ReadonlySet<BuildJobStatus>>([
  [
    BuildJobStatus.QUEUED,
    new Set([BuildJobStatus.BUILDING, BuildJobStatus.REJECTED]),
  ],
  [
    BuildJobStatus.BUILDING,
    new Set([
      BuildJobStatus.TESTING,
      BuildJobStatus.BUILD_FAILED,
      BuildJobStatus.REJECTED,
    ]),
  ],
  [
    BuildJobStatus.TESTING,
    new Set([
      BuildJobStatus.READY_FOR_APPROVAL,
      BuildJobStatus.QA_FAILED,
      BuildJobStatus.REJECTED,
    ]),
  ],
  [
    BuildJobStatus.REPAIRING,
    new Set([
      BuildJobStatus.TESTING,
      BuildJobStatus.REJECTED,
    ]),
  ],
  [
    BuildJobStatus.BUILD_FAILED,
    new Set([
      BuildJobStatus.QUEUED, // retry
      BuildJobStatus.REJECTED, // exhausted retries
    ]),
  ],
  [
    BuildJobStatus.QA_FAILED,
    new Set([
      BuildJobStatus.REPAIRING,
      BuildJobStatus.REJECTED,
    ]),
  ],
  [
    BuildJobStatus.READY_FOR_APPROVAL,
    new Set([
      BuildJobStatus.APPROVED,
      BuildJobStatus.REJECTED,
      BuildJobStatus.BUILDING, // rebuild requested
    ]),
  ],
  [
    BuildJobStatus.APPROVED,
    new Set([
      BuildJobStatus.PUBLISHED,
      BuildJobStatus.READY_FOR_APPROVAL, // rollback
    ]),
  ],
  [
    BuildJobStatus.PUBLISHED,
    new Set([]), // terminal
  ],
  [
    BuildJobStatus.REJECTED,
    new Set([
      BuildJobStatus.QUEUED, // manual re-queue
    ]),
  ],
]);

export interface BuildJobTransitionResult {
  allowed: boolean;
  from: BuildJobStatus;
  to: BuildJobStatus;
  reason?: string;
}

/**
 * Check if a transition from one build job status to another is allowed.
 */
export function canTransitionBuildJob(
  from: BuildJobStatus,
  to: BuildJobStatus,
): BuildJobTransitionResult {
  if (from === to) {
    return { allowed: true, from, to, reason: 'no-op transition' };
  }

  const allowed = BUILD_JOB_ALLOWED_TRANSITIONS.get(from);
  if (!allowed) {
    return { allowed: false, from, to, reason: `unknown source status: ${from}` };
  }

  if (allowed.has(to)) {
    return { allowed: true, from, to };
  }

  return {
    allowed: false,
    from,
    to,
    reason: `transition ${from} -> ${to} is not allowed. Allowed targets: ${[...allowed].join(', ') || '(none)'}`,
  };
}

/**
 * Attempt a build job status transition. Throws if not allowed.
 * Use this in mutation paths to enforce the state machine.
 */
export function transitionBuildJob(
  from: BuildJobStatus,
  to: BuildJobStatus,
): BuildJobStatus {
  const result = canTransitionBuildJob(from, to);
  if (!result.allowed) {
    throw new IllegalBuildJobStatusTransitionError(result);
  }
  return to;
}

export class IllegalBuildJobStatusTransitionError extends Error {
  public readonly from: BuildJobStatus;
  public readonly to: BuildJobStatus;

  constructor(result: BuildJobTransitionResult) {
    super(
      `Illegal build job status transition: ${result.from} -> ${result.to}. ${result.reason ?? ''}`,
    );
    this.name = 'IllegalBuildJobStatusTransitionError';
    this.from = result.from;
    this.to = result.to;
  }
}

/** Get all possible next statuses for a given build job status. */
export function getNextBuildJobStatuses(status: BuildJobStatus): BuildJobStatus[] {
  const allowed = BUILD_JOB_ALLOWED_TRANSITIONS.get(status);
  return allowed ? [...allowed] : [];
}

/** Check if a build job is in a terminal state (no further transitions possible). */
export function isBuildJobTerminal(status: BuildJobStatus): boolean {
  const allowed = BUILD_JOB_ALLOWED_TRANSITIONS.get(status);
  return !allowed || allowed.size === 0;
}

/** Check if a build job is in a failure state. */
export function isBuildJobFailed(status: BuildJobStatus): boolean {
  return (
    status === BuildJobStatus.BUILD_FAILED ||
    status === BuildJobStatus.QA_FAILED ||
    status === BuildJobStatus.REJECTED
  );
}

/** Check if a build job is in an active (non-terminal, non-failed) state. */
export function isBuildJobActive(status: BuildJobStatus): boolean {
  return (
    status === BuildJobStatus.QUEUED ||
    status === BuildJobStatus.BUILDING ||
    status === BuildJobStatus.TESTING ||
    status === BuildJobStatus.REPAIRING
  );
}
